// src/pages/Gallery.js
import React, { useState } from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardMedia,
  Modal,
  IconButton,
  useTheme,
  useMediaQuery,
  styled,
  Tabs,
  Tab,
  Fade,
  CircularProgress,
} from '@mui/material';
import {
  Close,
  PlayCircleOutline,
  NavigateNext,
  NavigateBefore,
  ZoomIn,
} from '@mui/icons-material';

// Styled Components
const StyledCard = styled(Card)(({ theme }) => ({
  position: 'relative',
  borderRadius: '16px',
  overflow: 'hidden',
  cursor: 'pointer',
  transition: 'all 0.3s ease-in-out',
  '&:hover': {
    transform: 'scale(1.02)',
    '& .media-overlay': {
      opacity: 1,
    },
  },
  '&::after': {
    content: '""',
    position: 'absolute',
    bottom: 0,
    left: 0,
    right: 0,
    height: '30%',
    background: 'linear-gradient(to top, rgba(0,0,0,0.7) 0%, rgba(0,0,0,0) 100%)',
  },
}));

const MediaOverlay = styled(Box)(({ theme }) => ({
  position: 'absolute',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  backgroundColor: 'rgba(0, 0, 0, 0.3)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  opacity: 0,
  transition: 'opacity 0.3s ease-in-out',
  zIndex: 1,
}));

const StyledModal = styled(Modal)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  '& .modal-content': {
    position: 'relative',
    maxWidth: '90vw',
    maxHeight: '90vh',
    outline: 'none',
    borderRadius: '16px',
    overflow: 'hidden',
    backgroundColor: theme.palette.background.paper,
  },
}));

const NavigationButton = styled(IconButton)(({ theme }) => ({
  position: 'absolute',
  top: '50%',
  transform: 'translateY(-50%)',
  backgroundColor: 'rgba(255, 255, 255, 0.1)',
  color: 'white',
  '&:hover': {
    backgroundColor: 'rgba(255, 255, 255, 0.2)',
  },
}));

const StyledTabs = styled(Tabs)(({ theme }) => ({
  marginBottom: theme.spacing(4),
  '& .MuiTab-root': {
    fontSize: '1.1rem',
    fontWeight: 600,
    textTransform: 'none',
    minWidth: 100,
    '&.Mui-selected': {
      color: theme.palette.primary.main,
    },
  },
  '& .MuiTabs-indicator': {
    height: 3,
    borderRadius: '1.5px',
  },
}));

// Gallery data
const galleryData = {
  photos: [
    {
      id: 1,
      title: 'Garden View',
      url: '/images/garden-view.jpg',
      category: 'Outdoor',
    },
    {
      id: 2,
      title: 'Luxury Suite',
      url: '/images/luxury-suite.jpg',
      category: 'Rooms',
    },
    // Add more photos...
  ],
  videos: [
    {
      id: 1,
      title: 'Resort Tour',
      thumbnail: '/images/tour-thumbnail.jpg',
      url: '/videos/resort-tour.mp4',
      duration: '2:30',
    },
    // Add more videos...
  ],
};

// Category options
const categories = ['All', 'Outdoor', 'Rooms', 'Dining', 'Amenities'];
function Gallery() {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const [activeTab, setActiveTab] = useState(0); // 0: Photos, 1: Videos
    const [selectedCategory, setSelectedCategory] = useState('All');
    const [selectedMedia, setSelectedMedia] = useState(null);
    const [loading, setLoading] = useState(true);
  
    // Filter media based on category
    const filteredPhotos = galleryData.photos.filter(
      photo => selectedCategory === 'All' || photo.category === selectedCategory
    );
  
    // Modal handlers
    const handleOpenMedia = (media) => setSelectedMedia(media);
    const handleCloseMedia = () => setSelectedMedia(null);
  
    // Navigation handlers
    const handlePrevious = () => {
      const currentIndex = filteredPhotos.findIndex(photo => photo.id === selectedMedia.id);
      const previousIndex = (currentIndex - 1 + filteredPhotos.length) % filteredPhotos.length;
      setSelectedMedia(filteredPhotos[previousIndex]);
    };
  
    const handleNext = () => {
      const currentIndex = filteredPhotos.findIndex(photo => photo.id === selectedMedia.id);
      const nextIndex = (currentIndex + 1) % filteredPhotos.length;
      setSelectedMedia(filteredPhotos[nextIndex]);
    };
  
    // Masonry layout for photos
    const PhotoGrid = () => (
      <Fade in={activeTab === 0}>
        <Grid container spacing={3}>
          {filteredPhotos.map((photo, index) => (
            <Grid 
              item 
              xs={12} 
              sm={6} 
              md={4} 
              key={photo.id}
              sx={{ 
                height: index % 3 === 0 ? 400 : index % 3 === 1 ? 300 : 350 
              }}
            >
              <StyledCard 
                onClick={() => handleOpenMedia(photo)}
                sx={{ height: '100%' }}
              >
                <CardMedia
                  component="img"
                  image={photo.url}
                  alt={photo.title}
                  sx={{
                    height: '100%',
                    objectFit: 'cover',
                  }}
                  onLoad={() => setLoading(false)}
                />
                <MediaOverlay className="media-overlay">
                  <ZoomIn sx={{ fontSize: 40, color: 'white' }} />
                </MediaOverlay>
                <Box
                  sx={{
                    position: 'absolute',
                    bottom: 0,
                    left: 0,
                    right: 0,
                    p: 2,
                    zIndex: 2,
                  }}
                >
                  <Typography 
                    variant="h6" 
                    color="white"
                    sx={{ 
                      textShadow: '2px 2px 4px rgba(0,0,0,0.5)',
                      fontWeight: 600
                    }}
                  >
                    {photo.title}
                  </Typography>
                  <Typography 
                    variant="body2" 
                    color="rgba(255,255,255,0.8)"
                    sx={{ textShadow: '1px 1px 2px rgba(0,0,0,0.5)' }}
                  >
                    {photo.category}
                  </Typography>
                </Box>
              </StyledCard>
            </Grid>
          ))}
        </Grid>
      </Fade>
    );
  
    // Video grid layout
    const VideoGrid = () => (
      <Fade in={activeTab === 1}>
        <Grid container spacing={3}>
          {galleryData.videos.map((video) => (
            <Grid item xs={12} sm={6} md={4} key={video.id}>
              <StyledCard onClick={() => handleOpenMedia(video)}>
                <CardMedia
                  component="img"
                  image={video.thumbnail}
                  alt={video.title}
                  sx={{ height: 250 }}
                />
                <MediaOverlay className="media-overlay">
                  <PlayCircleOutline sx={{ fontSize: 60, color: 'white' }} />
                </MediaOverlay>
                <Box
                  sx={{
                    position: 'absolute',
                    bottom: 0,
                    left: 0,
                    right: 0,
                    p: 2,
                    zIndex: 2,
                  }}
                >
                  <Typography 
                    variant="h6" 
                    color="white"
                    sx={{ textShadow: '2px 2px 4px rgba(0,0,0,0.5)' }}
                  >
                    {video.title}
                  </Typography>
                  <Typography 
                    variant="body2" 
                    color="rgba(255,255,255,0.8)"
                    sx={{ textShadow: '1px 1px 2px rgba(0,0,0,0.5)' }}
                  >
                    Duration: {video.duration}
                  </Typography>
                </Box>
              </StyledCard>
            </Grid>
          ))}
        </Grid>
      </Fade>
    );
      // Media Viewer Modal
  const MediaViewer = () => (
    <StyledModal
      open={Boolean(selectedMedia)}
      onClose={handleCloseMedia}
      closeAfterTransition
    >
      <Fade in={Boolean(selectedMedia)}>
        <Box className="modal-content">
          {/* Close button */}
          <IconButton
            onClick={handleCloseMedia}
            sx={{
              position: 'absolute',
              right: 16,
              top: 16,
              backgroundColor: 'rgba(0, 0, 0, 0.5)',
              color: 'white',
              zIndex: 2,
              '&:hover': {
                backgroundColor: 'rgba(0, 0, 0, 0.7)',
              },
            }}
          >
            <Close />
          </IconButton>

          {/* Media content */}
          {selectedMedia && (
            <Box sx={{ position: 'relative' }}>
              {activeTab === 0 ? (
                // Photo viewer
                <Box
                  component="img"
                  src={selectedMedia.url}
                  alt={selectedMedia.title}
                  sx={{
                    width: '100%',
                    height: 'auto',
                    maxHeight: '90vh',
                    objectFit: 'contain',
                  }}
                />
              ) : (
                // Video player
                <Box
                  component="video"
                  src={selectedMedia.url}
                  controls
                  autoPlay
                  sx={{
                    width: '100%',
                    maxHeight: '90vh',
                  }}
                />
              )}

              {/* Navigation buttons - only for photos */}
              {activeTab === 0 && (
                <>
                  <NavigationButton
                    onClick={handlePrevious}
                    sx={{ left: 16 }}
                  >
                    <NavigateBefore />
                  </NavigationButton>
                  <NavigationButton
                    onClick={handleNext}
                    sx={{ right: 16 }}
                  >
                    <NavigateNext />
                  </NavigationButton>
                </>
              )}

              {/* Media info */}
              <Box
                sx={{
                  position: 'absolute',
                  bottom: 0,
                  left: 0,
                  right: 0,
                  p: 3,
                  background: 'linear-gradient(to top, rgba(0,0,0,0.7) 0%, rgba(0,0,0,0) 100%)',
                }}
              >
                <Typography variant="h5" color="white" gutterBottom>
                  {selectedMedia.title}
                </Typography>
                {activeTab === 0 && (
                  <Typography variant="body1" color="rgba(255,255,255,0.8)">
                    {selectedMedia.category}
                  </Typography>
                )}
              </Box>
            </Box>
          )}
        </Box>
      </Fade>
    </StyledModal>
  );

  // Main render
  return (
    <Box
      sx={{
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #f5f7fa 0%, #e4e9f2 100%)',
        py: 8,
      }}
    >
      <Container maxWidth="xl">
        {/* Header */}
        <Typography
          variant={isMobile ? "h3" : "h2"}
          component="h1"
          textAlign="center"
          sx={{
            mb: 6,
            fontWeight: 700,
            background: 'linear-gradient(45deg, #1a237e 30%, #4caf50 90%)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
          }}
        >
          Our Resort Gallery
        </Typography>

        {/* Tabs */}
        <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 4 }}>
          <StyledTabs
            value={activeTab}
            onChange={(_, newValue) => setActiveTab(newValue)}
            centered
          >
            <Tab 
              label="Photos" 
              icon={<ZoomIn />} 
              iconPosition="start"
            />
            <Tab 
              label="Videos" 
              icon={<PlayCircleOutline />} 
              iconPosition="start"
            />
          </StyledTabs>
        </Box>

        {/* Category filter - only for photos */}
        {activeTab === 0 && (
          <Box 
            sx={{ 
              mb: 4,
              display: 'flex',
              justifyContent: 'center',
              flexWrap: 'wrap',
              gap: 2,
            }}
          >
            {categories.map((category) => (
              <Box
                key={category}
                onClick={() => setSelectedCategory(category)}
                sx={{
                  px: 3,
                  py: 1.5,
                  borderRadius: '25px',
                  cursor: 'pointer',
                  backgroundColor: selectedCategory === category 
                    ? 'primary.main' 
                    : 'rgba(255,255,255,0.9)',
                  color: selectedCategory === category 
                    ? 'white' 
                    : 'text.primary',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    transform: 'translateY(-2px)',
                    boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                  },
                  fontWeight: 600,
                }}
              >
                {category}
              </Box>
            ))}
          </Box>
        )}

        {/* Loading state */}
        {loading && (
          <Box 
            sx={{ 
              display: 'flex', 
              justifyContent: 'center',
              alignItems: 'center',
              minHeight: 400,
            }}
          >
            <CircularProgress />
          </Box>
        )}

        {/* Content */}
        <Box sx={{ mt: 4 }}>
          {activeTab === 0 ? <PhotoGrid /> : <VideoGrid />}
        </Box>

        {/* Media viewer modal */}
        <MediaViewer />
      </Container>
    </Box>
  );
}

export default Gallery;